import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import Pagination from "@/components/Pagination";
import { formatTRY, formatDate, CATEGORY_LABELS, CATEGORY_COLORS, METHOD_LABELS } from "@/components/finance/financeLabels";
import { Search, Ban } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

export default function LedgerPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ date_from: "", date_to: "", category: "", method: "" });

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE });
            Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
            const r = await api.get(`/admin/finance/ledger?${params.toString()}`);
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, page]);
    useEffect(() => { setPage(1); }, [filters]);

    const voidEntry = async (id) => {
        const reason = prompt("İptal sebebi:");
        if (!reason) return;
        try {
            await api.post(`/admin/finance/ledger/${id}/void`, { reason });
            toast.success("Kayıt iptal edildi");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İptal başarısız");
        }
    };

    return (
        <FinanceShell>
            <div className="flex flex-wrap gap-2 mb-4">
                <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="ledger-from" />
                <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="ledger-to" />
                <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="ledger-category">
                    <option value="">Tüm Kategoriler</option>
                    {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <select value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="ledger-method">
                    <option value="">Tüm Yöntemler</option>
                    {Object.entries(METHOD_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
            </div>

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                {loading ? <div className="py-10 text-center text-slate-500">Yükleniyor...</div> : items.length === 0 ? (
                    <div className="py-12 text-center text-slate-500">Bu filtreye uygun kayıt yok.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="ledger-table">
                            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Fiş No</th>
                                    <th className="px-3 py-2 text-left">Tarih</th>
                                    <th className="px-3 py-2 text-left">Açıklama</th>
                                    <th className="px-3 py-2 text-left">Kategori</th>
                                    <th className="px-3 py-2 text-left">Borç</th>
                                    <th className="px-3 py-2 text-left">Alacak</th>
                                    <th className="px-3 py-2 text-right">Tutar</th>
                                    <th className="px-3 py-2 text-center">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.id} className={`border-t border-slate-100 dark:border-slate-800/50 ${it.is_void ? "opacity-50 line-through" : ""}`} data-testid={`ledger-row-${it.entry_no}`}>
                                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{it.entry_no}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(it.date)}</td>
                                        <td className="px-3 py-2 max-w-[280px] truncate" title={it.description}>{it.description || "—"}</td>
                                        <td className={`px-3 py-2 text-xs font-semibold ${CATEGORY_COLORS[it.category] || ""}`}>{CATEGORY_LABELS[it.category] || it.category}</td>
                                        <td className="px-3 py-2 text-[11px] text-slate-600 dark:text-slate-300">{it.debit_party_id_label || it.debit_party_id?.slice(0,8)}</td>
                                        <td className="px-3 py-2 text-[11px] text-slate-600 dark:text-slate-300">{it.credit_party_id_label || it.credit_party_id?.slice(0,8)}</td>
                                        <td className="px-3 py-2 text-right font-bold whitespace-nowrap">{formatTRY(it.amount_try)}</td>
                                        <td className="px-3 py-2 text-center">
                                            {!it.is_void && (
                                                <button onClick={() => voidEntry(it.id)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:underline" data-testid={`void-${it.entry_no}`}>
                                                    <Ban className="h-3 w-3" /> İptal
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="kayıt" />
            </div>
        </FinanceShell>
    );
}
